// src/components/admin/SlideMenu.tsx
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  List, ListItemButton, ListItemIcon, ListItemText,
  Box, IconButton, Chip, Collapse, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import SettingsIcon from '@mui/icons-material/Settings';
import GavelIcon from '@mui/icons-material/Gavel';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import AddCircleOutlineTwoToneIcon from '@mui/icons-material/AddCircleOutlineTwoTone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupIcon from '@mui/icons-material/Group';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StarsIcon from '@mui/icons-material/Stars';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SecurityIcon from '@mui/icons-material/Security';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LayersIcon from '@mui/icons-material/Layers';
import DiscountIcon from '@mui/icons-material/Discount';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import Whatshot from '@mui/icons-material/Whatshot';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PercentIcon from '@mui/icons-material/Percent';
import ShieldIcon from '@mui/icons-material/Shield';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/VOTOlogo.png';
import PaymentsIcon from '@mui/icons-material/Payments';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import WorkIcon from '@mui/icons-material/Work';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
// ✅ Import icons for Agent Details and User Monitor
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';

interface SlideMenuProps {
  mobileOpen: boolean;
  onToggle: () => void;
}

type PermMap = Record<string, { canView: boolean; canWrite: boolean }>;

// ── Menu groups, in the display order they should appear ─────────────────────
const GROUP_DEFS: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: 'users',             label: 'Users',                  icon: <GroupIcon /> },
  { key: 'product_catalog',   label: 'Product Catalog',        icon: <InventoryIcon /> },
  { key: 'orders',            label: 'Orders',                 icon: <GavelIcon /> },
  { key: 'delivery_shipping', label: 'Delivery & Shipping',    icon: <AirportShuttleIcon /> },
  { key: 'marketing',         label: 'Marketing & Promotions', icon: <DiscountIcon /> },
  { key: 'rewards',           label: 'Rewards',                icon: <CardGiftcardIcon /> },
  { key: 'reports',           label: 'Reports',                icon: <AssessmentIcon /> },
  { key: 'access_control',    label: 'Access Control',         icon: <SecurityIcon /> },
  { key: 'settings',          label: 'Settings',                icon: <SettingsIcon /> },
  { key: 'support',           label: 'Support',                icon: <HeadsetMicIcon /> },
];

const ALL_MENUS = [
  // Dashboard (rendered as a standalone top-level item, not inside an accordion)
  { id: 'dashboard',                    name: 'Dashboard',                    path: '/adminDashboard',             icon: <DashboardCustomizeOutlinedIcon />, group: 'top' },

  // Users
  { id: 'user_list',                    name: 'User List',                    path: '/Userlist',                   icon: <GroupIcon />,                     group: 'users' },
  // Not mandatory for now — uncomment to bring these back into the Users menu.
  // { id: 'guest_order_list',             name: 'Guest User Details',           path: '/guestOrderlist',             icon: <AccountCircleIcon />,             group: 'users' },
  // { id: 'guest_user_order',             name: 'Guest Users Order List',       path: '/guestUserOrder',             icon: <PeopleAltIcon />,                 group: 'users' },
  { id: 'user_monitor',                 name: 'User Monitor',                 path: '/userMonitor',                icon: <MonitorHeartIcon />,              group: 'users' },
  { id: 'user_wallet',                  name: 'User Wallet',                  path: '/userWallet',                 icon: <AccountBalanceWalletIcon />,      group: 'users' },
  { id: 'set_credit',                   name: 'Set Credit for User',          path: '/SetCredit',                  icon: <CreditScoreOutlinedIcon />,       group: 'users' },
  { id: 'agent_details',                name: 'Agent Details',                path: '/agentDetails',               icon: <AssignmentIndOutlinedIcon />,     group: 'users' },
  { id: 'refer_friend',                 name: 'Refer a Friend',               path: '/referFriend',                icon: <PeopleIcon />,                    group: 'users' },

  // Product Catalog
  { id: 'product_management',           name: 'Manage Product List',          path: '/productManagement',          icon: <InventoryIcon />,                 group: 'product_catalog' },
  { id: 'category_management',          name: 'Category Management',          path: '/categoryManagement',         icon: <CategoryOutlinedIcon />,          group: 'product_catalog' },
  { id: 'subcategory_management',       name: 'Sub Category Management',      path: '/subcategorymanagement',      icon: <CategoryOutlinedIcon />,          group: 'product_catalog' },
  { id: 'bundle_management',            name: 'Bundle Management',            path: '/bundleManagment',            icon: <LayersIcon />,                    group: 'product_catalog' },
  { id: 'unit_management',              name: 'Unit Management',              path: '/unitManagement',             icon: <AddCircleOutlineTwoToneIcon />,   group: 'product_catalog' },
  { id: 'warehouse_management',         name: 'Warehouse Management',         path: '/WarehouseManagement',        icon: <WarehouseIcon />,                 group: 'product_catalog' },
  { id: 'product_warehouse_management', name: 'Product Warehouse Management', path: '/ProductWarehouseManagement', icon: <StorefrontIcon />,                group: 'product_catalog' },
  { id: 'ext_warranty_products',        name: 'Ext. Warranty Products List',  path: '/extWarrantyProducts',        icon: <ShieldIcon />,                    group: 'product_catalog' },
  { id: 'extended_warranty',            name: 'Extended Warranty Management', path: '/extendedWarranty',           icon: <ShieldIcon />,                    group: 'product_catalog' },

  // Orders
  { id: 'order_management',             name: 'Order Management',             path: '/adminOrderManagement',       icon: <GavelIcon />,                     group: 'orders' },
  { id: 'bulk_order_management',        name: 'Bulk Order Request',           path: '/bulkOrderManagement',        icon: <ShoppingCartIcon />,              group: 'orders' },
  { id: 'return_process_management',    name: 'Return Process Management',    path: '/returnprocessManagement',    icon: <AssignmentReturnIcon />,          group: 'orders' },
  { id: 'pickupPending',                name: 'Set to Agent Pick UP',         path: '/pickupPending',              icon: <AutoFixHighIcon />,               group: 'orders' },
  { id: 'review',                       name: 'Review',                       path: '/review',                     icon: <StarsIcon />,                     group: 'orders' },

  // Delivery & Shipping
  { id: 'delivery_agent_management',    name: 'Delivery Agent Management',    path: '/DeliveryAgentManagement',    icon: <AirportShuttleIcon />,            group: 'delivery_shipping' },
  { id: 'delivery_management',          name: 'Delivery Management',          path: '/deliveryManagement',         icon: <AirportShuttleIcon />,            group: 'delivery_shipping' },
  { id: 'delivery_settings',            name: 'Delivery Settings',            path: '/deliverySettings',           icon: <SettingsIcon />,                  group: 'delivery_shipping' },
  { id: 'shipping_management',          name: 'Shipping Management',          path: '/shippingManagement',         icon: <PublicOutlinedIcon />,            group: 'delivery_shipping' },

  // Marketing & Promotions
  { id: 'coupon_management',            name: 'Coupon Management',            path: '/couponManagement',           icon: <DiscountIcon />,                  group: 'marketing' },
  { id: 'admin_coupon_users',           name: 'Coupons Users List',           path: '/AdminCouponUsers',           icon: <PeopleAltIcon />,                 group: 'marketing' },
  { id: 'deal_of_the_day',              name: 'Deal of the Day',              path: '/dealoftheday',               icon: <AutoAwesomeIcon />,               group: 'marketing' },
  { id: 'flash_sale',                   name: 'Flash Sale',                   path: '/flashSale',                  icon: <Whatshot />,                      group: 'marketing' },
  { id: 'banner_management',            name: 'Banner Management',            path: '/banner',                     icon: <ViewCarouselOutlinedIcon />,      group: 'marketing' },
  { id: 'membership_plan_management',   name: 'Membership Plans',             path: '/membershipPlanManagement',   icon: <WorkspacePremiumIcon />,          group: 'marketing' },

  // Rewards
  { id: 'reward_management',            name: 'Reward Amount Conversions',    path: '/rewardManagement',           icon: <MilitaryTechIcon />,              group: 'rewards' },
  { id: 'reward_settings_management',   name: 'Reward Point Settings',        path: '/RewardSettingsManagement',   icon: <CardGiftcardIcon />,              group: 'rewards' },
  { id: 'admin_rewards',                name: 'User Reward Management',       path: '/AdminRewards',               icon: <ManageAccountsIcon />,            group: 'rewards' },

  // Reports
  { id: 'reports_management',           name: 'Reports Management',           path: '/reportsManagement',          icon: <AssessmentIcon />,                group: 'reports' },
  { id: 'reports_list',                 name: 'Reports List',                 path: '/reportsList',                icon: <ListAltIcon />,                   group: 'reports' },

  // Access Control
  { id: 'admin_user_list',              name: 'BackOffice User List',         path: '/adminUserList',              icon: <PeopleAltIcon />,                 group: 'access_control' },
  { id: 'role_master',                  name: 'Role Master',                  path: '/roleMaster',                 icon: <SecurityIcon />,                  group: 'access_control' },
  { id: 'role_mapping',                 name: 'Role Mapping',                 path: '/roleMapping',                icon: <AssignmentIndIcon />,             group: 'access_control' },

  // Settings
  { id: 'tax_settings',                 name: 'Tax Settings',                 path: '/taxSettings',                icon: <PercentIcon />,                   group: 'settings' },
  { id: 'smtp_settings',                name: 'SMTP Settings',                path: '/smtpsettings',               icon: <EmailIcon />,                     group: 'settings' },
  { id: 'site_settings',                name: 'Site Settings',                path: '/siteSettings',               icon: <LanguageIcon />,                  group: 'settings' },
  { id: 'cash_on_delivery',             name: 'Cash on Delivery',             path: '/cashOnDelivery',             icon: <PaymentsIcon />,                  group: 'settings' },
  { id: 'about_us',                     name: 'About Us',                     path: '/aboutUsPage',                icon: <InfoIcon />,                      group: 'settings' },
  { id: 'services',                     name: 'Services',                     path: '/servicePage',                icon: <BuildIcon />,                     group: 'settings' },
  { id: 'career_management',            name: 'Career Management',            path: '/careerManagement',           icon: <WorkIcon />,                      group: 'settings' },

  // Support
   { id: 'loyalty_program',              name: 'Loyalty Program',              path: '/LoyaltyProgramManagement',    icon: <CardGiftcardIcon />,             group: 'rewards' },
  { id: 'support_management',           name: 'Help/Resolution Support',      path: '/supportManagement',          icon: <HeadsetMicIcon />,                group: 'support' },
] as const;

// ── Path-to-group helpers ─────────────────────────────────────────────────────
const GROUP_PATHS: Record<string, string[]> = Object.fromEntries(
  GROUP_DEFS.map(g => [g.key, ALL_MENUS.filter(m => m.group === g.key).map(m => m.path)])
);

function isActiveIn(paths: string[], pathname: string) {
  return paths.includes(pathname);
}

function parseRights(rights: string | string[]): { canView: boolean; canWrite: boolean } {
  const arr = Array.isArray(rights)
    ? rights.map(r => r.trim().toLowerCase())
    : rights.split(',').map(r => r.trim().toLowerCase());
  return {
    canView:  arr.includes('view'),
    canWrite: arr.some(r => ['add', 'edit', 'delete', 'write'].includes(r)),
  };
}

function parsePermissions(
  moduleOrScreen: Record<string, string | string[]> | string | null | undefined
): PermMap {
  if (!moduleOrScreen) return {};
  const permMap: PermMap = {};
  if (moduleOrScreen === 'all') {
    ALL_MENUS.forEach(({ id }) => { permMap[id] = { canView: true, canWrite: true }; });
    return permMap;
  }
  if (typeof moduleOrScreen === 'object' && !Array.isArray(moduleOrScreen)) {
    Object.entries(moduleOrScreen).forEach(([menuId, rightsVal]) => {
      permMap[menuId] = parseRights(rightsVal as string | string[]);
    });
    return permMap;
  }
  if (typeof moduleOrScreen === 'string') {
    try { return parsePermissions(JSON.parse(moduleOrScreen)); }
    catch {
      moduleOrScreen.split(',').forEach(id => {
        const t = id.trim();
        if (t) permMap[t] = { canView: true, canWrite: false };
      });
    }
  }
  return permMap;
}

// ─────────────────────────────────────────────────────────────────
const SlideMenu: React.FC<SlideMenuProps> = ({ mobileOpen, onToggle }) => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const pathname  = location.pathname;

  // Open/closed state for every accordion group, keyed by GROUP_DEFS[].key.
  // Starts empty (all closed); the useEffect below opens whichever group
  // contains the active route once permissions have loaded.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (key: string) => {
    // Capture the sidebar's current scroll offset *before* the Collapse
    // expands/collapses, so we can pin it back afterwards (see the
    // useLayoutEffect below). Without this, expanding a group lower in the
    // list (e.g. Settings) can steal focus and cause the browser to
    // auto-scroll the list down to the newly revealed section, abandoning
    // whatever screen (e.g. Delivery Agent Management) the user was on.
    if (listRef.current) {
      scrollPositionRef.current = listRef.current.scrollTop;
    }
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const [permMap,           setPermMap]           = useState<PermMap>({});
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const listRef          = useRef<HTMLUListElement>(null);
  const activeItemRef    = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const rawRole = (
    localStorage.getItem('admin_type') ||
    localStorage.getItem('user_role')  ||
    localStorage.getItem('role')       ||
    ''
  ).toLowerCase().trim();

  const normalizeRole = (r: string) => {
    if (!r || r === 'new' || r === 'unknown') return 'admin';
    if (r.includes('super')) return 'super_admin';
    if (r === 'operation' || r === 'vendor') return r;
    return 'admin';
  };

  const userRole     = normalizeRole(rawRole);
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin      = userRole === 'admin';
  const isOperation  = userRole === 'operation';
  const isVendor     = userRole === 'vendor';
  const displayRole  = localStorage.getItem('role') || rawRole || 'Admin';

  // ── Expand the correct group whenever permissions finish loading (so
  //    conditional groups like Coupons/Admin/GlobalSettings are mounted
  //    by the time we try to open them) OR whenever the route changes.
  useEffect(() => {
    if (!permissionsLoaded) return;          // wait until groups are actually rendered
    GROUP_DEFS.forEach(g => {
      if (isActiveIn(GROUP_PATHS[g.key], pathname)) {
        setOpenGroups(prev => (prev[g.key] ? prev : { ...prev, [g.key]: true }));
      }
    });
  }, [pathname, permissionsLoaded]);

  // ── After permissions load AND the relevant submenu has expanded, scroll
  //    the active item into view — but ONLY the very first time (e.g. on a
  //    hard page refresh or when landing on a deep link). If this re-ran on
  //    every route change it would yank the sidebar's scroll position back
  //    to the active item on every click, which is exactly the "jumps to
  //    top / loses my place" behaviour that isn't user-friendly. Once the
  //    user is browsing the menu themselves, we leave scroll position alone
  //    so whatever they clicked simply stays where it is.
  const hasAutoScrolledRef = useRef(false);
  useEffect(() => {
    if (!permissionsLoaded || hasAutoScrolledRef.current) return;
    // Wait for MUI Collapse animation to finish (default 300ms) + buffer.
    const id = setTimeout(() => {
      if (activeItemRef.current) {
        activeItemRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      hasAutoScrolledRef.current = true;
    }, 400);
    return () => clearTimeout(id);
  }, [permissionsLoaded, openGroups]);

  // ── Pin the sidebar's scroll position back to where it was right before
  //    a group was expanded/collapsed or a menu item was clicked. This runs
  //    synchronously before the browser paints, so it overrides any native
  //    "scroll the newly-focused/expanded element into view" behaviour that
  //    would otherwise yank the list down to whatever section just opened.
  //    (It's intentionally skipped on the very first render — before the
  //    user has interacted at all — so it doesn't fight the one-time
  //    initial auto-center effect above.)
  const hasInteractedRef = useRef(false);
  useLayoutEffect(() => {
    if (!hasInteractedRef.current) return;
    if (listRef.current) {
      listRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [openGroups, pathname]);

  const markInteracted = () => { hasInteractedRef.current = true; };

  useEffect(() => {
    if (isOperation) {
      const allowed = ['/productManagement', '/adminOrderManagement', '/bulkOrderManagement'];
      if (!allowed.includes(pathname)) navigate('/productManagement', { replace: true });
    }
    if (isVendor && pathname !== '/productManagement') {
      navigate('/productManagement', { replace: true });
    }
  }, [isOperation, isVendor, pathname, navigate]);

  useEffect(() => {
    const build = async () => {
      const setFull = (): PermMap => {
        const m: PermMap = {};
        ALL_MENUS.forEach(({ id }) => { m[id] = { canView: true, canWrite: true }; });
        return m;
      };

      let resolved: PermMap = {};

      // Vendors are intentionally restricted to Product Management only.
      if (isVendor) {
        resolved = { product_management: { canView: true, canWrite: true } };
      } else if (isSuperAdmin) {
        resolved = setFull();
      } else {
        const savedRaw = localStorage.getItem('admin_permissions');
        if (savedRaw && savedRaw !== 'null' && savedRaw !== 'undefined') {
          try { resolved = parsePermissions(JSON.parse(savedRaw)); }
          catch { resolved = setFull(); }
        } else {
          const token  = localStorage.getItem('admin_token');
          const roleId = localStorage.getItem('admin_role_id');
          if (!token || !roleId || roleId === 'null') {
            resolved = setFull();
          } else {
            try {
              const res  = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/permissions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, authkey: import.meta.env.VITE_AUTH_KEY },
                body: JSON.stringify({ role_id: Number(roleId) }),
              });
              const data = await res.json();
              if (!data.error && data.permissions?.length > 0) {
                data.permissions.forEach((perm: any) => {
                  if (perm.module_or_screen) resolved = { ...resolved, ...parsePermissions(perm.module_or_screen) };
                });
              } else {
                resolved = setFull();
              }
            } catch { resolved = setFull(); }
          }
        }
      }

      setPermMap(resolved);
      localStorage.setItem('admin_perm_map', JSON.stringify(resolved));
      setPermissionsLoaded(true);
    };

    build();
  }, [userRole]);

  const canView  = useCallback((id: string) => permMap[id]?.canView  ?? false, [permMap]);
  const canWrite = useCallback((id: string) => permMap[id]?.canWrite ?? false, [permMap]);
  const groupHasAccess = useCallback(
    (group: string) => ALL_MENUS.some(m => m.group === group && canView(m.id)),
    [canView]
  );

  // Brand look pulled straight from voto.css :root tokens, so the admin
  // sidebar reads as the same product as the storefront (orange accent,
  // Montserrat, same radii/shadow/transition curve).
  const menuStyle = {
    py: 1.25,
    px: 2,
    pl: 2.25,                                   // leaves room for the 3px selected accent bar below
    borderRadius: 'var(--radius-sm, 8px)',
    mx: 1,
    mb: 0.5,
    color: 'var(--dark, #1A1A1A)',
    fontSize: '14px',
    fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
    borderLeft: '3px solid transparent',
    transition: 'background-color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1)), color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1)), border-color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
    '&:hover': {
      backgroundColor: 'rgba(247,99,56,0.06)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(247,99,56,0.10)',
      color: 'var(--orange-dark, #e94c1c)',
      fontWeight: 600,
      borderLeft: '3px solid var(--orange, #F76338)',
      '&:hover': {
        backgroundColor: 'rgba(247,99,56,0.14)',
      },
      '& .MuiListItemIcon-root': {
        color: 'var(--orange-dark, #e94c1c)',
      },
      '& .MuiListItemText-primary': {
        fontWeight: 600,
      },
    },
  };

  const handleMenuClick = () => {
    markInteracted();
    if (listRef.current) {
      scrollPositionRef.current = listRef.current.scrollTop;
    }
  };

  const handleMenuMouseDown = () => {
    markInteracted();
    if (listRef.current) {
      scrollPositionRef.current = listRef.current.scrollTop;
    }
  };

  // ── MenuRow – attaches activeItemRef when this item is selected so the
  //    scroll-into-view effect can target it directly.
  const MenuRow = ({
    id, name, path, icon, indent = false, bold = false,
  }: { id: string; name: string; path: string; icon: React.ReactNode; indent?: boolean; bold?: boolean }) => {
    const isSelected = pathname === path;

    if (!canView(id)) return null;

    return (
      <ListItemButton
        ref={isSelected ? activeItemRef : undefined}
        component={Link}
        to={path}
        selected={isSelected}
        onClick={handleMenuClick}
        onMouseDown={handleMenuMouseDown}
        sx={{ ...menuStyle, ...(indent ? { pl: 5 } : {}) }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{icon}</ListItemIcon>
        <ListItemText
          primary={name}
          primaryTypographyProps={{
            fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
            fontSize: bold ? '0.95rem' : '0.9rem',
            fontWeight: bold || isSelected ? 600 : 400,
            color: bold && !isSelected ? 'var(--dark, #1A1A1A)' : 'inherit'
          }}
        />
      </ListItemButton>
    );
  };

  const SubMenuGroup = ({
    label, icon, open, onToggle: toggle, children,
  }: { label: string; icon: React.ReactNode; open: boolean; onToggle: () => void; children: React.ReactNode }) => (
    <>
      <ListItemButton
        onClick={toggle}
        onMouseDown={handleMenuMouseDown}
        sx={{ ...menuStyle, borderLeft: '3px solid transparent' }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: open ? 'var(--orange-dark, #e94c1c)' : 'var(--gray, #6B6B6B)' }}>{icon}</ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
            fontSize: '0.95rem',
            fontWeight: 700,
            color: 'var(--dark, #1A1A1A)'
          }}
        />
        {open ? <ExpandLessIcon sx={{ color: 'var(--gray, #6B6B6B)' }} /> : <ExpandMoreIcon sx={{ color: 'var(--gray, #6B6B6B)' }} />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{
          backgroundColor: 'var(--light-gray, #fafafa)',
          borderRadius: 'var(--radius-sm, 8px)',
          mx: 1,
          mb: 0.5,
        }}>
          {children}
        </List>
      </Collapse>
    </>
  );

  return (
    <Box sx={{
      width: 260,
      height: '100vh',
      backgroundColor: 'var(--white, #FFFFFF)',
      borderRight: '1px solid var(--border, #E8E8E8)',
      fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'sticky', // stays pinned in the viewport while the page content scrolls,
      top: 0,             // regardless of whether a given page's own wrapper scrolls the
      left: 0,            // whole document or just an inner panel.
      alignSelf: 'flex-start', // prevent flex "stretch" from breaking the sticky height
      flexShrink: 0,
      zIndex: 5,
    }}>
      <Box sx={{ p: 2, borderBottom: '1px solid var(--border, #E8E8E8)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <img src={logo} alt="VOTO Logo" style={{ height: 40 }} />
        {mobileOpen && <IconButton onClick={onToggle} size="small"><CloseIcon /></IconButton>}
      </Box>

      {/* Role chip */}
      {permissionsLoaded && (
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Chip
            icon={
              isVendor     ? <StorefrontIcon        sx={{ fontSize: 18, color: '#fff !important' }} /> :
              isSuperAdmin ? <AdminPanelSettingsIcon sx={{ fontSize: 18, color: '#fff !important' }} /> :
              isAdmin      ? <SecurityIcon           sx={{ fontSize: 18, color: '#fff !important' }} /> :
                             <AssignmentIndIcon      sx={{ fontSize: 18, color: '#fff !important' }} />
            }
            label={displayRole}
            sx={{
              width: '100%',
              backgroundColor: isVendor ? '#4caf50' : isSuperAdmin ? 'var(--orange-dark, #e94c1c)' : isAdmin ? '#2196f3' : '#ff9800',
              color: '#fff',
              fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              borderRadius: '20px',
              boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
            }}
          />
        </Box>
      )}

      {/* Spinner overlay */}
      {!permissionsLoaded && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'var(--white, #FFFFFF)', zIndex: 10 }}>
          <CircularProgress size={28} sx={{ color: 'var(--orange, #F76338)' }} />
        </Box>
      )}

      {/* Menu list – always mounted to preserve scroll position */}
      <List
        ref={listRef}
        onScroll={() => {
          if (listRef.current) {
            scrollPositionRef.current = listRef.current.scrollTop;
          }
        }}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: 1,
          scrollBehavior: 'instant',
          overflowAnchor: 'none',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'var(--orange, #F76338)' },
        }}
      >
        <MenuRow id="dashboard" name="Dashboard" path="/adminDashboard" icon={<DashboardCustomizeOutlinedIcon />} bold />

        {GROUP_DEFS.map(g => groupHasAccess(g.key) && (
          <SubMenuGroup
            key={g.key}
            label={g.label}
            icon={g.icon}
            open={!!openGroups[g.key]}
            onToggle={() => toggleGroup(g.key)}
          >
            {ALL_MENUS.filter(m => m.group === g.key).map(m => (
              <MenuRow key={m.id} id={m.id} name={m.name} path={m.path} icon={m.icon} indent />
            ))}
          </SubMenuGroup>
        ))}
      </List>
    </Box>
  );
};

export default SlideMenu;